import React from 'react';
import { Shield, Award, CheckCircle, ThumbsUp, FileCheck, Car } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const iconMap = {
  Shield, Award, CheckCircle, ThumbsUp, FileCheck, Car
};

// Unified to the brand palette (red on light red) — no rainbow chips.
const colorMap = {
  Shield: { color: 'text-red-600', bg: 'bg-red-50' },
  FileCheck: { color: 'text-red-600', bg: 'bg-red-50' },
  Award: { color: 'text-red-600', bg: 'bg-red-50' },
  ThumbsUp: { color: 'text-red-600', bg: 'bg-red-50' },
  Car: { color: 'text-red-600', bg: 'bg-red-50' },
  CheckCircle: { color: 'text-red-600', bg: 'bg-red-50' }
};

const TrustBadges = ({ variant = 'horizontal' }) => {
  const { settings } = useSiteSettings();

  // Get settings with defaults
  const enabled = settings.showTrustBadges !== false;
  const badges = (settings.trustBadges || [
    { icon: "Shield", title: "Fully Inspected", subtitle: "Every vehicle checked", enabled: true },
    { icon: "FileCheck", title: "Clean Title", subtitle: "Verified history", enabled: true },
    { icon: "Award", title: "Quality Guaranteed", subtitle: "Satisfaction promise", enabled: true },
    { icon: "ThumbsUp", title: "No Hidden Fees", subtitle: "Transparent pricing", enabled: true },
    { icon: "Car", title: "Test Drive", subtitle: "Try before you buy", enabled: true },
    { icon: "CheckCircle", title: "Easy Financing", subtitle: "Quick approval", enabled: true }
  ]).filter(b => b.enabled !== false);

  if (!enabled || badges.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap justify-center gap-4 py-4">
        {badges.slice(0, 4).map((badge, index) => {
          const colors = colorMap[badge.icon] || colorMap.Shield;
          return (
            <div key={index} className="flex items-center gap-2 text-gray-600">
              {iconMap[badge.icon] && React.createElement(iconMap[badge.icon], { size: 18, className: colors.color })}
              <span className="text-sm font-medium">{badge.title}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <section className="bg-gray-50 py-8 border-y border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {badges.map((badge, index) => {
            const IconComponent = iconMap[badge.icon] || Shield;
            const colors = colorMap[badge.icon] || colorMap.Shield;
            
            return (
              <div 
                key={index} 
                className="flex flex-col items-center text-center group"
                data-testid={`trust-badge-${index}`}
              >
                <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <IconComponent size={28} className={colors.color} />
                </div>
                <p className="font-semibold text-gray-800 text-sm">{badge.title}</p>
                <p className="text-gray-500 text-xs">{badge.subtitle}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustBadges;
